import { formatDate, formatTimeOfDay, lastMomentBefore } from './format.js';
import { FIELD_STATE_LABELS, FIELD_STATE_MARKERS } from './labels.js';
/**
 * Turn a **decided** field state into what the page shows.
 *
 * Split from the decision on purpose. The server owns the state (concept 11.9:
 * "Fristen entscheidet ausschliesslich der Server"), so the participant frontend
 * renders the state it was given and must not re-derive it from clocks and
 * deadlines. What is left here is wording, marker and interaction: presentation,
 * not authority.
 *
 * `projectFieldState` below decides the state locally and then calls this. That
 * path stays for the backoffice preview, which has no request context to ask the
 * server about.
 */
export function presentFieldState(input) {
    const { state, timing, timeZone } = input;
    const graceWindowEndsAt = input.graceWindowEndsAt ?? null;
    switch (state) {
        case 'depublished':
            return present(state, {
                interaction: 'none',
                showsContent: false,
                showsAnswerForm: false,
                notice: {
                    kind: 'unavailable',
                    // Generic wording towards participants: details stay internal
                    // (.claude/rules/conventions.md).
                    text: 'Inhalt derzeit nicht verfügbar.',
                },
                graceWindowEndsAt: null,
            });
        case 'locked':
            return present(state, {
                interaction: 'none',
                showsContent: false,
                showsAnswerForm: false,
                notice: {
                    kind: 'locked',
                    text: `Öffnet am ${formatDate(timing.unlocksAt, timeZone)}.`,
                },
                graceWindowEndsAt: null,
            });
        case 'today':
            return present(state, {
                interaction: 'participate',
                showsContent: true,
                showsAnswerForm: true,
                notice: {
                    kind: 'deadline_today',
                    text: `Teilnahme an der Verlosung heute bis ${formatTimeOfDay(lastMomentBefore(timing.participationEndsAt), timeZone)} Uhr.`,
                },
                graceWindowEndsAt: null,
            });
        case 'past_participated':
            return present(state, {
                interaction: 'content_only',
                showsContent: true,
                showsAnswerForm: false,
                notice: {
                    kind: 'participation_recorded',
                    text: drawPoolText(input.includedInDraw ?? null),
                },
                graceWindowEndsAt: null,
            });
        case 'closed':
            return present(state, {
                interaction: 'content_only',
                showsContent: true,
                showsAnswerForm: false,
                notice: {
                    kind: 'archived',
                    text: 'Diese Aktion ist abgeschlossen. Eine Teilnahme ist nicht mehr möglich.',
                },
                graceWindowEndsAt: null,
            });
        case 'past_open': {
            // A day that has passed opens like any other, and says first that its draw is over.
            // Individual overlay, not a state of its own: if this visitor still has a
            // draft inside the grace window, they make it into today's pool, so they
            // must not be told the day is over (concept 9.6.1).
            const notice = graceWindowEndsAt === null
                ? {
                    kind: 'draw_closed',
                    text: 'Dieser Tag ist vorbei. An der Verlosung können Sie nicht mehr teilnehmen.',
                }
                : {
                    kind: 'grace_window',
                    text: `Ihre Teilnahme an der Verlosung ist noch bis ${formatTimeOfDay(graceWindowEndsAt, timeZone)} Uhr abzuschließen.`,
                };
            return present(state, {
                interaction: 'lead_only',
                showsContent: true,
                showsAnswerForm: true,
                notice,
                graceWindowEndsAt,
            });
        }
    }
}
/**
 * Projection rule (concept 9.6.1): decide the state, then present it.
 *
 * The order of the checks *is* the specification. One exception sits on top: an
 * emergency stop on field level sets `depublished` and overrides every other
 * state. That is the only operating decision that reaches the participant, which
 * is why it is checked first and nowhere else.
 *
 * Pure function on purpose: no clock access, no locale guessing, no I/O.
 *
 * The participant frontend does **not** call this; it renders the state the API
 * decided (`presentFieldState`). This is the backoffice preview path, where there
 * is no request to ask about and the point is to show what a given moment yields.
 */
export function projectFieldState(input) {
    const { timing, isDepublished, now, timeZone } = input;
    const viewer = input.viewer;
    const nowMs = now.getTime();
    const unlocksAtMs = Date.parse(timing.unlocksAt);
    const participationEndsAtMs = Date.parse(timing.participationEndsAt);
    const followUpEndsAtMs = Date.parse(timing.followUpEndsAt);
    const state = deriveState({
        isDepublished,
        nowMs,
        unlocksAtMs,
        participationEndsAtMs,
        followUpEndsAtMs,
        hasParticipation: viewer?.hasParticipation ?? false,
    });
    return presentFieldState({
        state,
        timing,
        timeZone,
        includedInDraw: viewer?.includedInDraw ?? null,
        graceWindowEndsAt: state === 'past_open'
            ? activeGraceWindowEnd(viewer?.draft?.graceWindowEndsAt, nowMs)
            : null,
    });
}
function deriveState(input) {
    if (input.isDepublished) {
        return 'depublished';
    }
    if (input.nowMs < input.unlocksAtMs) {
        return 'locked';
    }
    if (input.nowMs <= input.participationEndsAtMs) {
        return 'today';
    }
    // Daily deadline has passed.
    //
    // Tie-break decision, NOT resolved by concept 9.6.1: after the follow-up
    // deadline a recorded participation matches both "vergangen, teilgenommen"
    // and "geschlossen", although the states are declared mutually exclusive.
    // Resolved here in favour of the participation: there is nothing left to do
    // either way, and a participant should keep seeing their own answer and prize
    // status rather than an anonymous archive tile. The Python resolver decides it
    // the same way (`countdown.services.field_timeline.field_state`), and
    // contracts/field-state-cases.json holds both sides to it.
    if (input.hasParticipation) {
        return 'past_participated';
    }
    if (input.nowMs > input.followUpEndsAtMs) {
        return 'closed';
    }
    // The deliberately open late-comer lead track (concept 9.2).
    return 'past_open';
}
function activeGraceWindowEnd(graceWindowEndsAt, nowMs) {
    if (graceWindowEndsAt === undefined) {
        return null;
    }
    const endsAtMs = Date.parse(graceWindowEndsAt);
    if (Number.isNaN(endsAtMs) || nowMs >= endsAtMs) {
        return null;
    }
    return graceWindowEndsAt;
}
function drawPoolText(includedInDraw) {
    if (includedInDraw === true) {
        return 'Ihre Teilnahme ist verbucht und wurde in der Tagesverlosung berücksichtigt.';
    }
    if (includedInDraw === false) {
        return 'Ihre Teilnahme ist verbucht, wurde in der Tagesverlosung aber nicht mehr berücksichtigt.';
    }
    return 'Ihre Teilnahme ist verbucht.';
}
function present(state, rest) {
    return {
        state,
        label: FIELD_STATE_LABELS[state],
        marker: FIELD_STATE_MARKERS[state],
        ...rest,
    };
}
//# sourceMappingURL=project-field-state.js.map